import React, { PropTypes } from 'react';
import { connect } from 'react-redux';
import { find, findIndex } from 'lodash';
import styled from 'styled-components';
import { NitroSearchBar, NitroLoading, NitroIcon } from 'nitro-components';

import Filter from '../Filter';

import { getFilterCountries } from '../../../../utils/globalSelectors';
import { pushGAAction } from '../../../../utils/gtm';

const Input = styled.div`
  display: flex;
  padding: 12px 16px;
  border: 1px solid ${props => props.theme.color.border};
  background-color: ${props => props.theme.color.white};
`;

const InputLabel = styled.div`
  color: ${props =>
    props.placeholder
      ? props.theme.color.base3
      : props.theme.color[props.variant]};
  min-width: 280px;
  font-family: ${props =>
    props.placeholder ? props.theme.font.default : props.theme.font.bold};
`;

const ListContainer = styled.section`
  margin-top: 12px;
`;

const ListHeader = styled.div`
  display: flex;
  padding: 8px 16px;
  background-color: ${props => props.theme.color[props.variant]};
  color: ${props => props.theme.color.white};
`;

const BackIcon = styled.div`
  cursor: pointer;
  margin-right: 8px;
`;

const ListHeaderTitle = styled.div`
  flex: 1;
  line-height: 24px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ListHeaderDescription = styled.div`
  margin-left: 8px;
  text-align: right;
  white-space: nowrap;
  color: ${props => props.theme.color.white};
  opacity: 0.5;
  line-height: 24px;
  font-size: 12px;
`;

const List = styled.ul`
  position: relative;
  margin: 0;
  padding: 0 16px;
  max-height: 350px;
  min-height: 200px;
  overflow: auto;
  color: ${props => props.theme.color.base2};
  background-color: ${props => props.theme.color.white};
  border: 1px solid ${props => props.theme.color.border};
  border-top: none;
  list-style: none;
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.color.border};
`;

const ListItemLabel = styled.span`
  flex: 1;
  cursor: pointer;
  margin: 0 10px 0 16px;
  font-family: ${props =>
    props.active ? props.theme.font.bold : props.theme.font.default};
  color: ${props => props.active && props.theme.color[props.variant]};
`;

const NextLevel = styled.span`
  visibility: ${props => (props.hide ? 'hidden' : 'visible')};
`;

const ExpandLabel = styled.span`
  font-size: 12px;
  vertical-align: middle;
  margin-right: 8px;
  color: ${props => props.theme.color.base3};
`;

const GoNextLevel = styled.span`
  display: inline-block;
  cursor: pointer;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  background-color: #f7f7f7;
  border: 1px solid ${props => props.theme.color.border};
  border-radius: 2px;
  &:hover {
    border-color: ${props => props.theme.color.base3};
    .icon {
      fill: ${props => props.theme.color.base2};
    }
  }
`;

const getCostcenters = (country, countries) => {
  let costcenters = find(countries, { id: country.value });
  if (costcenters) {
    costcenters = costcenters.children;
  } else {
    costcenters = [];
  }
  return costcenters;
};

class Costcenter extends Filter {
  constructor(props) {
    super(props);

    const country = find(props.filters, { title: 'Country' });

    let costcenters = getCostcenters(country, props.countries);
    if (props.rootCostcenter && costcenters && costcenters.length > 0) {
      const selCC = find(costcenters, { id: props.rootCostcenter.value });
      costcenters =
        props.rootCostcenter && typeof selCC !== 'undefined'
          ? [selCC]
          : costcenters;
    }
    if (props.availableCompanies && props.availableCompanies.length > 0) {
      costcenters = props.availableCompanies;
    }

    this.state = {
      roots: [],
      fetchingCostcentres: false,
      country,
      costcenters
    };
  }

  componentDidMount() {
    if (!this.listRef) return;

    this.listRef.addEventListener('scroll', this.loadMore);
  }

  componentWillUnmount() {
    this.listRef.removeEventListener('scroll', this.loadMore);
  }

  loadMore = () => {
    const { scrollTop, clientHeight, scrollHeight } = this.listRef;
    if (scrollTop + clientHeight >= scrollHeight && this.props.loadMore) {
      this.props.loadMore();
    }
  };

  // Detect country change change the default costcenter
  componentWillReceiveProps(nextProps) {
    const newCountry = find(nextProps.filters, { title: 'Country' });
    const { countries, rootCostcenter, availableCompanies } = this.props;

    // If root costcenter changed, set new costcenter list
    if (rootCostcenter) {
      if (nextProps.rootCostcenter.value !== rootCostcenter.value) {
        let costcenters = getCostcenters(newCountry, countries);
        const rootCostcenter = find(costcenters, {
          id: nextProps.rootCostcenter.value
        });
        costcenters = rootCostcenter ? [rootCostcenter] : [];
        return this.setState({ costcenters, roots: [] });
      }
    }

    // If country changed, set root costcenter list
    if (newCountry.value !== this.state.country.value) {
      const costcenters = getCostcenters(newCountry, countries);
      if (costcenters.length > 0) {
        const { id, title } = costcenters[0];
        this.props.action({
          label: title,
          value: id
        });
      } else {
        this.props.action({
          label: null,
          value: null
        });
      }

      this.setState({
        country: newCountry,
        costcenters,
        roots: []
      });
    }

    if (nextProps.availableCompanies !== availableCompanies) {
      const costcenters = nextProps.availableCompanies;
      return this.setState({ costcenters, roots: [] });
    }
  }

  openCostcentre = costcenter => {
    const rootCostcenter = Object.assign({}, costcenter, {
      parent: [...this.state.costcenters]
    });
    const roots = [...this.state.roots, rootCostcenter];
    this.setState({
      roots,
      costcenters: [],
      fetchingCostcentres: true
    });

    this.getCostcenters(rootCostcenter);
  };

  getCostcenters = (costcenter, searchText) => {
    let data = costcenter.children;
    if (searchText) {
      const regex = new RegExp(searchText, 'i');
      data = data.filter(x => regex.test(x.id) || regex.test(x.title));
    }
    if (this.root) {
      this.setState({
        costcenters: data,
        fetchingCostcentres: false
      });
    }
  };

  setCostcentre = costcenter => {
    pushGAAction('FILTER', `CostCenterSelection::${costcenter.id}`);

    this.props.action({
      value: costcenter.id,
      label: costcenter.title
    });
  };

  goBack = () => {
    const roots = [...this.state.roots];
    const costcenters = roots.pop().parent;
    this.setState({ roots, costcenters });
  };

  search = searchText => {
    this.setState({
      fetchingCostcentres: true
    });

    const { roots } = this.state;
    const parentCostcentre = Object.assign({}, roots[roots.length - 1]);
    this.getCostcenters(parentCostcentre, searchText.toLowerCase());
  };

  doAvailLookup = searchText => {
    if (this.props.doAvailLookup) {
      this.props.doAvailLookup(searchText);
    }
  };

  searchAvail = searchText => {};

  render() {
    const variant = this.props.variant || 'secondary';

    const { availableCompanies, lookup, availableCompaniesCount } = this.props;
    const { label, value } = this.props.filter;
    const { costcenters, fetchingCostcentres } = this.state;

    const { roots } = this.state;
    const parentCostcentre = roots.length > 0 ? roots[roots.length - 1] : null;

    const costcenterList =
      !costcenters || costcenters.length === 0
        ? []
        : costcenters.map(costcenter => {
            const selected = costcenter && costcenter.id === value;

            const checkIcon = selected ? 'success-circle' : 'circle';

            return (
              <ListItem key={costcenter.id}>
                <NitroIcon
                  size={12}
                  variant={variant}
                  name={checkIcon}
                  action={() => this.setCostcentre(costcenter)}
                />

                <ListItemLabel
                  variant={variant}
                  onClick={() => this.setCostcentre(costcenter)}
                  active={selected}
                >
                  {costcenter.title}
                </ListItemLabel>
                <NextLevel
                  hide={this.props.noDrillDown || costcenter.total === 0}
                >
                  <ExpandLabel>{costcenter.total}</ExpandLabel>
                  <GoNextLevel
                    id={`cc-go-next-${costcenter.id}`}
                    onClick={() => this.openCostcentre(costcenter)}
                  >
                    <NitroIcon
                      variant="base3"
                      size={8}
                      name="arrow-right"
                      clickable
                    />
                  </GoNextLevel>
                </NextLevel>
              </ListItem>
            );
          });

    const title = parentCostcentre ? parentCostcentre.title : 'Company codes';
    const totalCostcentres =
      availableCompaniesCount ||
      costcenters.reduce(
        (acc, costcenter) => acc + (costcenter.total + 1 || 1),
        0
      );

    const showLookupSearch =
      lookup !== '' || (availableCompanies && availableCompanies.length > 0);

    return (
      <section
        ref={ref => {
          this.root = ref;
        }}
      >
        <Input>
          <InputLabel variant={variant} placeholder={!value}>
            {label || 'No company code specified'}
          </InputLabel>
        </Input>

        <ListContainer>
          {parentCostcentre && (
            <NitroSearchBar
              block
              hintText={`Search company code in ${parentCostcentre.title}`}
              action={this.search}
            />
          )}

          {showLookupSearch && (
            <NitroSearchBar
              block
              value={lookup}
              hintText={lookup !== '' ? lookup : 'Search company code'}
              action={text => this.doAvailLookup(text)}
            />
          )}
        </ListContainer>

        <ListContainer>
          <ListHeader variant={variant}>
            {parentCostcentre && (
              <BackIcon onClick={this.goBack}>
                <NitroIcon name="arrow-back" variant="white" size={12} />
              </BackIcon>
            )}
            <ListHeaderTitle>{title}</ListHeaderTitle>
            {!fetchingCostcentres && (
              <ListHeaderDescription>
                {totalCostcentres} company codes
              </ListHeaderDescription>
            )}
          </ListHeader>

          <List innerRef={node => (this.listRef = node)}>
            {fetchingCostcentres ? <NitroLoading center /> : costcenterList}
          </List>
        </ListContainer>
      </section>
    );
  }
}

Costcenter.propTypes = {
  action: PropTypes.func,
  filter: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  filters: PropTypes.array,
  variant: PropTypes.string,
  rootCostcenter: PropTypes.object,
  doAvailLookup: PropTypes.func
};

Costcenter.getLabel = Filter.getLabel;

const mapStateToProps = (state, ownProps) => {
  const countries = getFilterCountries(state);

  let mainSelection = countries;
  if (typeof ownProps.availableCompanies !== 'undefined') {
    const country = find(ownProps.filters, { title: 'Country' });
    const selCtry = findIndex(countries, { id: country.value });

    const finalCountry = {
      ...countries[selCtry],
      children: ownProps.availableCompanies
    };

    mainSelection = [finalCountry];
  }
  return {
    countries: mainSelection
  };
};

export default connect(mapStateToProps, null)(Costcenter);



// WEBPACK FOOTER //
// ./src/components/filter/filters/cost-center/Costcenter.js